if (!customElements.get('product-card-gallery')) {
  customElements.define('product-card-gallery', class ProductCardGallery extends HTMLElement {
    constructor() {
      super();

      this.track = this.querySelector('.product-card-gallery__track');
      this.slides = Array.from(this.querySelectorAll('[data-card-gallery-slide]'));
      this.previewTrack = this.querySelector('.product-card-gallery__preview-track');
      this.previews = Array.from(this.querySelectorAll('[data-card-gallery-preview]'));
      this.progressFill = this.querySelector('.product-card-gallery__progress-fill');
      this.previousButton = this.querySelector('.product-card-gallery__button--prev');
      this.nextButton = this.querySelector('.product-card-gallery__button--next');
      this.activeIndex = 0;
      this.scrollFrame = null;

      if (!this.track || this.slides.length < 2) return;

      this.previews.forEach((preview) => {
        preview.addEventListener('click', () => this.goTo(Number(preview.dataset.cardGalleryPreview)));
      });
      this.previousButton?.addEventListener('click', () => {
        const isWrapping = this.activeIndex === 0;
        this.goTo((this.activeIndex - 1 + this.slides.length) % this.slides.length, !isWrapping);
      });
      this.nextButton?.addEventListener('click', () => {
        const isWrapping = this.activeIndex === this.slides.length - 1;
        this.goTo((this.activeIndex + 1) % this.slides.length, !isWrapping);
      });
      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      this.track.addEventListener('dragstart', (event) => event.preventDefault());
      this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.track.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.track.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.track.addEventListener('pointercancel', this.onPointerUp.bind(this));
      this.track.addEventListener('click', (event) => {
        if (!this.didDrag) return;
        event.preventDefault();
        this.didDrag = false;
      }, true);
      this.previewTrack?.addEventListener('scroll', this.updatePreviewOverflow.bind(this), { passive: true });

      this.resizeObserver = new ResizeObserver(() => {
        this.updatePreviewViewportWidth();
        this.updatePreviewOverflow();
        this.goTo(this.activeIndex, false);
      });
      this.resizeObserver.observe(this);

      this.updatePreviewViewportWidth();
      this.updateState();
      this.updatePreviewOverflow();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    }

    onScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        const nextIndex = this.getNearestSlideIndex();

        if (nextIndex !== this.activeIndex) {
          this.activeIndex = nextIndex;
          this.updateState();
        }
      });
    }

    getNearestSlideIndex() {
      const scrollPosition = Math.abs(this.track.scrollLeft);
      return this.slides.reduce((closestIndex, slide, index) => {
        return Math.abs(slide.offsetLeft - scrollPosition) < Math.abs(this.slides[closestIndex].offsetLeft - scrollPosition)
          ? index
          : closestIndex;
      }, 0);
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      this.isPointerDragging = true;
      this.didDrag = false;
      this.pointerDragStartX = event.clientX;
      this.pointerDragStartScrollLeft = this.track.scrollLeft;
      this.track.setPointerCapture(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.isPointerDragging) return;

      const dragDistance = event.clientX - this.pointerDragStartX;
      if (Math.abs(dragDistance) < 4) return;

      this.didDrag = true;
      event.preventDefault();
      this.track.scrollLeft = this.pointerDragStartScrollLeft - dragDistance;
    }

    onPointerUp(event) {
      if (!this.isPointerDragging) return;

      this.isPointerDragging = false;
      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);

      if (this.didDrag) {
        this.goTo(this.getNearestSlideIndex());
        window.setTimeout(() => { this.didDrag = false; });
      }
    }

    goTo(index, smooth = true) {
      const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const nextSlide = this.slides[nextIndex];
      if (!nextSlide) return;

      this.activeIndex = nextIndex;
      this.track.scrollTo({
        left: document.documentElement.dir === 'rtl' ? nextSlide.offsetLeft * -1 : nextSlide.offsetLeft,
        behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant'
      });
      this.updateState();
    }

    updateState() {
      const progress = (this.activeIndex + 1) / this.slides.length;
      if (this.progressFill) this.progressFill.style.transform = `scaleX(${progress})`;

      this.slides.forEach((slide, index) => {
        const isActive = index === this.activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.toggleAttribute('aria-hidden', !isActive);
      });

      this.previews.forEach((preview, index) => {
        const isActive = index === this.activeIndex;
        preview.classList.toggle('is-active', isActive);
        preview.toggleAttribute('aria-current', isActive);
      });

      if (this.previousButton) this.previousButton.disabled = this.slides.length < 2;
      if (this.nextButton) this.nextButton.disabled = this.slides.length < 2;

      const activePreview = this.previews[this.activeIndex];
      if (activePreview && this.previewTrack) {
        const maxScroll = Math.max(0, this.previewTrack.scrollWidth - this.previewTrack.clientWidth);

        if (maxScroll > 2) {
          const scrollPosition = Math.abs(this.previewTrack.scrollLeft);
          const previewStart = activePreview.offsetLeft;
          const centeredPosition = previewStart - ((this.previewTrack.clientWidth - activePreview.offsetWidth) / 2);
          const targetPosition = Math.max(0, Math.min(centeredPosition, maxScroll));

          if (Math.abs(targetPosition - scrollPosition) > 2) {
            this.previewTrack.scrollTo({
              left: document.documentElement.dir === 'rtl' ? targetPosition * -1 : targetPosition,
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
            });
          }
        }
      }

      this.updatePreviewOverflow();
    }

    updatePreviewOverflow() {
      if (!this.previewTrack) return;

      const scrollPosition = Math.abs(this.previewTrack.scrollLeft);
      const maxScroll = Math.max(0, this.previewTrack.scrollWidth - this.previewTrack.clientWidth);
      const isOverflowing = maxScroll > 2;

      this.classList.toggle('is-overflowing', isOverflowing);
      this.classList.toggle('has-overflow-start', isOverflowing && scrollPosition > 2);
      this.classList.toggle('has-overflow-end', isOverflowing && scrollPosition < maxScroll - 2);
    }

    updatePreviewViewportWidth() {
      if (!this.previewTrack || this.previews.length < 1) return;

      this.style.removeProperty('--card-gallery-preview-item-width');
      this.style.removeProperty('--card-gallery-preview-width');

      const availableWidth = this.clientWidth;
      const naturalItemWidth = this.previews[0].getBoundingClientRect().width;
      if (!availableWidth || !naturalItemWidth) return;

      const trackStyles = window.getComputedStyle(this.previewTrack);
      const gap = parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
      const buttonWidth = Math.max(
        this.previousButton?.getBoundingClientRect().width || 0,
        this.nextButton?.getBoundingClientRect().width || 0
      );
      const controlClearance = 2;
      const maxViewportWidth = Math.max(
        naturalItemWidth,
        availableWidth - (2 * (buttonWidth + controlClearance))
      );

      let visibleCount = Math.max(
        1,
        Math.min(this.previews.length, Math.floor((maxViewportWidth + gap) / (naturalItemWidth + gap)))
      );
      let itemWidth = naturalItemWidth;

      const nextVisibleCount = Math.min(this.previews.length, visibleCount + 1);
      if (nextVisibleCount > visibleCount) {
        const fittedItemWidth = (
          maxViewportWidth - (Math.max(nextVisibleCount - 1, 0) * gap)
        ) / nextVisibleCount;

        if (fittedItemWidth >= naturalItemWidth * 0.9) {
          visibleCount = nextVisibleCount;
          itemWidth = fittedItemWidth;
        }
      }

      const viewportWidth = Math.min(
        maxViewportWidth,
        (visibleCount * itemWidth) + (Math.max(visibleCount - 1, 0) * gap)
      );

      this.style.setProperty('--card-gallery-preview-item-width', `${itemWidth}px`);
      this.style.setProperty('--card-gallery-preview-width', `${viewportWidth}px`);
    }
  });
}
